import React from 'react'
import cx from 'classnames'
import { BtnV2, Icon, Loader, l, TextArea } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { getInstagramChannel } from 'common/core/selectors/channelSelectors'

import { Comment } from './components/Comment'
import { useCommentsSwitching } from './hooks/useCommentsSwitching'
import { useQuestionnaire } from './hooks/useQuestionnaire'

import cm from './styles.module.css'

export const Questionnaire = () => {
  const { avatar } = useAppSelector(getInstagramChannel)

  const {
    samples,
    form,
    submitLike,
    submitDislike,
    showCommentForm,
    setShowCommentForm,
    activeSample,
    isStrikeBroken,
    showControls,
    isLoading,
    isFinished,
    currentStrike,
    totalCount,
    reset,
  } = useQuestionnaire()

  const {
    commentsRef,
    commentsWrapperRef,

    samplesRef,
    samplesWrapperRef,
  } = useCommentsSwitching({
    activeSample: isLoading ? -1 : activeSample,
  })

  const { formState, register, handleSubmit } = form

  const getCommentClassName = (index: number) =>
    cx(cm.commentWrapper, {
      [cm.commentWrapper_visible]: index === activeSample,
      [cm.commentWrapper_liked]: index > activeSample,
    })

  const onCancelCommentForm = () => {
    setShowCommentForm(false)
    reset()
  }

  return (
    <div className={cx('d-flex flex-col justify-between', !isFinished && 'h-full')}>
      <div>
        <h4 className="m-b-xs text-subheading">{l.translate('Review AI’s draft replies')}</h4>

        <p aria-live="polite" aria-atomic="true" className="m-b-lg text-body-sm text-secondary">
          {l.translate('Hit “Sounds good” {total} times in a row to set your tone', {
            total: totalCount,
          })}
          {` `}
          <Icon.Question
            size={16}
            className={cm.header__questionIcon}
            data-title={l.translate(
              'AI knows it’s nailed your voice when you hit “Sounds good” {total} times in a row',
              {
                total: totalCount,
              },
            )}
            data-title-at="top"
          />
        </p>

        <section className={cm.questionnaire}>
          <p className="m-b-sm font-medium">{l.translate('To a comment like this')}</p>

          <div className="p-relative" ref={commentsWrapperRef} aria-live="polite">
            {samples.map((sample, index) => (
              <div
                key={index}
                className={getCommentClassName(index)}
                ref={(element) => {
                  if (element) {
                    commentsRef.current[index] = element
                  }
                }}
                aria-hidden={index !== activeSample}
                // @ts-expect-error type for inert is supported starting from React 19+
                // It's also not working for now when we pass true/false, that's why there is an empty string and undefined values
                inert={index !== activeSample ? '' : undefined}
              >
                <Comment
                  avatar={null}
                  comment={
                    <span>
                      {l.translate('<span class="font-medium">Someone</span> commented:')}{' '}
                      {sample.comment.text}
                    </span>
                  }
                  variant="default"
                />
              </div>
            ))}
          </div>

          <p className="m-t-lg m-b-sm font-medium">{l.translate('AI would reply')}</p>
          {!isStrikeBroken && Boolean(currentStrike) && !isFinished && (
            <div className="d-flex gap-xs m-b-sm align-baseline">
              <p className={cm.strikeInfo__green}>
                {l.translate('{row}/{total} approved', {
                  row: currentStrike,
                  total: totalCount,
                })}
              </p>
              {totalCount - currentStrike === 1 && (
                <p className="m-t-0 text-body-sm text-secondary">
                  {l.translate('{left} more until your tone is set!', {
                    left: totalCount - currentStrike,
                  })}
                </p>
              )}
            </div>
          )}
          {isStrikeBroken && (
            <div className="d-flex gap-xs m-b-sm align-baseline">
              <p className={cm.strikeInfo__gray}>{l.translate('Streak reset')}</p>
              <p className="m-t-0 text-body-sm text-secondary">
                {l.translate('AI needs {total}/{total} to be confident it sounds like you', {
                  total: totalCount,
                })}
              </p>
            </div>
          )}

          <div className={cm.replyWrapper}>
            {isLoading && (
              <div className={cm.loader}>
                <Loader size={24} />
                <p className="text-secondary">{l.translate('Adjusting AI with your feedback…')}</p>
              </div>
            )}
            {!isLoading && (
              <div className="p-relative" ref={samplesWrapperRef} aria-live="polite">
                {samples.map((sample, index) => (
                  <div
                    key={index}
                    className={getCommentClassName(index)}
                    ref={(element) => {
                      if (element) {
                        samplesRef.current[index] = element
                      }
                    }}
                    aria-hidden={index !== activeSample}
                    // @ts-expect-error type for inert is supported starting from React 19+
                    // It's also not working for now when we pass true/false, that's why there is an empty string and undefined values
                    inert={index !== activeSample ? '' : undefined}
                  >
                    <Comment avatar={avatar} comment={sample.ai_reply.text} variant="light" />
                  </div>
                ))}
              </div>
            )}

            {showCommentForm && !isLoading && (
              <form className={cm.form} onSubmit={handleSubmit(submitDislike)}>
                <TextArea
                  label={l.translate('How can we make it sound more like you?')}
                  placeholder={l.translate('Describe what feels off')}
                  minRows={2}
                  maxRows={6}
                  autoFocus
                  {...register('user_reaction')}
                  invalid={Boolean(formState.errors.user_reaction?.message)}
                  errorText={formState.errors.user_reaction?.message}
                />

                <div className="d-flex justify-between m-t">
                  <BtnV2 onClick={onCancelCommentForm}>{l.translate('Cancel')}</BtnV2>

                  <BtnV2 variant="primary" type="submit">
                    {l.translate('Submit')}
                  </BtnV2>
                </div>
              </form>
            )}
          </div>
          {showControls && !isLoading && (
            <ul className={cm.questionnaire__controls} aria-label={l.translate('Review proposal')}>
              <li>
                <BtnV2 onClick={() => setShowCommentForm(true)} leftIcon="ThumbDown">
                  {l.translate('Not my style')}
                </BtnV2>
              </li>

              <li>
                <BtnV2 onClick={submitLike} leftIcon="ThumbUp">
                  {l.translate('Sounds good')}
                </BtnV2>
              </li>
            </ul>
          )}
        </section>
      </div>
      {!isFinished && (
        <p className="m-t-md text-body-sm text-secondary">
          {l.translate('Your feedback helps AI sound more natural and true to your style.')}
        </p>
      )}
    </div>
  )
}
